// Singlish → Sinhala transliteration helper
// Case matters for retroflex letters: T D N L S  (ට ඩ ණ ළ ෂ)  vs  t d n l s (ත ද න ල ස)

const HAL = '\u0DCA'; // ් (hal kirima / virama)
const ZWJ = '\u200D'; // zero-width joiner (for rakaaraansaya / yansaya)

// [roman, independent vowel, vowel sign]
const VOWEL_LIST = [
  ['aae', 'ඈ', 'ෑ'],
  ['aa', 'ආ', 'ා'],
  ['ae', 'ඇ', 'ැ'],
  ['ai', 'ඓ', 'ෛ'],
  ['au', 'ඖ', 'ෞ'],
  ['ii', 'ඊ', 'ී'],
  ['ee', 'ඒ', 'ේ'],
  ['oo', 'ඕ', 'ෝ'],
  ['uu', 'ඌ', 'ූ'],
  ['Ru', 'ඍ', 'ෘ'],
  ['a', 'අ', ''],
  ['i', 'ඉ', 'ි'],
  ['u', 'උ', 'ු'],
  ['e', 'එ', 'ෙ'],
  ['o', 'ඔ', 'ො'],
];

const CONSONANT_LIST = [
  ['chh', 'ඡ'], ['ndh', 'ඳ'], ['mbh', 'ඹ'],
  ['kh', 'ඛ'], ['gh', 'ඝ'], ['ng', 'ඟ'], ['ch', 'ච'], ['jh', 'ඣ'],
  ['Th', 'ඨ'], ['Dh', 'ඪ'], ['th', 'ත'], ['dh', 'ද'],
  ['ph', 'ඵ'], ['bh', 'භ'], ['sh', 'ශ'], ['Sh', 'ෂ'],
  ['GN', 'ඥ'], ['KN', 'ඤ'],
  ['k', 'ක'], ['g', 'ග'], ['c', 'ච'], ['j', 'ජ'],
  ['T', 'ට'], ['D', 'ඩ'], ['N', 'ණ'],
  ['t', 'ත'], ['d', 'ද'], ['n', 'න'],
  ['p', 'ප'], ['b', 'බ'], ['m', 'ම'],
  ['y', 'ය'], ['r', 'ර'], ['l', 'ල'], ['L', 'ළ'],
  ['v', 'ව'], ['w', 'ව'],
  ['s', 'ස'], ['S', 'ෂ'], ['h', 'හ'], ['f', 'ෆ'],
  ['z', 'ජ'], ['q', 'ක'],
];

const SYMBOLS = { x: 'ං' }; // anusvaraya  (e.g. "sixha" → සිංහ)

const byLength = (a, b) => b[0].length - a[0].length;
const VOWELS = [...VOWEL_LIST].sort(byLength);
const CONSONANTS = [...CONSONANT_LIST].sort(byLength);

/** Longest match at position i: exact case first, then case-insensitive for lowercase keys */
const matchAt = (str, i, table) => {
  for (const entry of table) {
    if (str.substr(i, entry[0].length) === entry[0]) return entry;
  }
  for (const entry of table) {
    const key = entry[0];
    if (key === key.toLowerCase() && str.substr(i, key.length).toLowerCase() === key) {
      return entry;
    }
  }
  return null;
};

/**
 * Convert a Singlish string to Sinhala.
 * Non-letters (spaces, digits, punctuation) and existing Sinhala pass through untouched.
 */
export function singlishToSinhala(input) {
  if (!input) return '';
  const s = String(input);
  let out = '';
  let i = 0;
  let prevEndsWithHal = false;

  while (i < s.length) {
    const ch = s[i];

    // 1. Consonant (+ optional vowel sign)
    const cons = matchAt(s, i, CONSONANTS);
    if (cons) {
      const [roman, sinhala] = cons;
      i += roman.length;

      // Conjunct: ක්‍ර (rakaaraansaya) / ක්‍ය (yansaya)
      const isConjunct = prevEndsWithHal && (sinhala === 'ර' || sinhala === 'ය');
      const base = isConjunct ? ZWJ + sinhala : sinhala;

      const vowel = matchAt(s, i, VOWELS);
      if (vowel) {
        out += base + vowel[2];
        i += vowel[0].length;
        prevEndsWithHal = false;
      } else {
        out += base + HAL;
        prevEndsWithHal = true;
      }
      continue;
    }

    // 2. Independent vowel
    const vowel = matchAt(s, i, VOWELS);
    if (vowel) {
      out += vowel[1];
      i += vowel[0].length;
      prevEndsWithHal = false;
      continue;
    }

    // 3. Symbols / pass-through
    out += SYMBOLS[ch] || ch;
    prevEndsWithHal = false;
    i += 1;
  }

  return out;
}

/**
 * Live typing support for a controlled <textarea>.
 *
 * Keeps the current Latin "word buffer" so partial input like "k" → "ක්" is
 * re-converted correctly when the next letter arrives ("ka" → "ක").
 *
 * @param prevValue  value currently shown in the textarea
 * @param nextValue  value the browser just produced (e.target.value)
 * @param state      { committed, buffer } from the previous call
 * @returns          { value, state }  → set value in form, store state in a ref
 */
export function applySinglishInput(prevValue, nextValue, state) {
  let st = state || { committed: '', buffer: '' };

  // Out of sync (form reset, mode switch, external change) → start fresh
  if (prevValue !== st.committed + singlishToSinhala(st.buffer)) {
    st = { committed: prevValue, buffer: '' };
  }

  const render = (s) => s.committed + singlishToSinhala(s.buffer);

  // Typing / pasting at the end
  if (nextValue.length > prevValue.length && nextValue.startsWith(prevValue)) {
    let { committed, buffer } = st;
    for (const ch of nextValue.slice(prevValue.length)) {
      if (/[A-Za-z]/.test(ch)) {
        buffer += ch;
      } else {
        committed = committed + singlishToSinhala(buffer) + ch;
        buffer = '';
      }
    }
    const next = { committed, buffer };
    return { value: render(next), state: next };
  }

  // Backspace at the end while a word is in progress
  if (st.buffer && nextValue.length < prevValue.length && prevValue.startsWith(nextValue)) {
    const next = { committed: st.committed, buffer: st.buffer.slice(0, -1) };
    return { value: render(next), state: next };
  }

  // Anything else (mid-text edit, selection delete, etc.) → accept as-is
  return { value: nextValue, state: { committed: nextValue, buffer: '' } };
}